/**
 * Fehlertolerante Kontaktsuche (phonetisch leicht, Teilstrings, Tippfehler).
 * Reine String-Logik ohne DB — für clientseitige Filterung der geladenen Liste geeignet.
 */

const WORD_SPLIT = /[^\p{L}\d]+/gu;

/**
 * Kleinschreibung + Diakritika entfernen ("Sören" → "soren").
 * Zusätzlich „ß“ → „ss“ (NFD entfernt ß nicht zuverlässig als ss).
 * @param {unknown} v
 */
export function foldContactSearchAscii(v) {
  let s = String(v ?? "");
  s = s.replace(/\u00df/gi, "ss").replace(/\u1e9e/g, "ss");
  try {
    s = s.normalize("NFD").replace(/\p{M}/gu, "");
  } catch (_) {
    s = s.toLowerCase();
    return s;
  }
  return s.toLowerCase();
}

/**
 * @param {unknown} v
 */
export function digitsOnlyFromString(v) {
  return String(v ?? "").replace(/\D/g, "");
}

/**
 * @param {string} foldedFull — bereits gefalteter Haystack
 * @returns {string[]}
 */
export function extractFoldedCorpusWords(foldedFull) {
  const raw = String(foldedFull ?? "").split(WORD_SPLIT).filter(Boolean);
  /** @type {Set<string>} */
  const seen = new Set();
  /** @type {string[]} */
  const out = [];
  for (const w of raw) {
    const k = w.trim();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(k);
  }
  return out;
}

/** @param {string} a @param {string} b */
function trigramDiceSimilarity(a, b) {
  const x = String(a ?? "");
  const y = String(b ?? "");
  if (!x.length || !y.length) return 0;
  if (x === y) return 1;

  const grams = (str) => {
    const s = `  ${str} `;
    /** @type {Map<string, number>} */
    const m = new Map();
    for (let i = 0; i < s.length - 2; i++) {
      const tri = s.slice(i, i + 3);
      m.set(tri, (m.get(tri) || 0) + 1);
    }
    return m;
  };

  const Ax = grams(x);
  const Bx = grams(y);
  let dot = 0;
  let a2 = 0;
  let b2 = 0;
  for (const v of Ax.values()) {
    a2 += v * v;
  }
  for (const v of Bx.values()) {
    b2 += v * v;
  }
  for (const [k, va] of Ax) {
    const vb = Bx.get(k);
    if (vb) dot += va * vb;
  }
  const den = Math.sqrt(a2) * Math.sqrt(b2);
  return den > 0 ? dot / den : 0;
}

/** Klein-Strings: Levenshtein-basierte Näherung ergänzend zu Trigrams. */
function levenshteinRatio(a, b) {
  const s = String(a ?? "");
  const t = String(b ?? "");
  if (!s.length || !t.length) return 0;
  const m = s.length;
  const n = t.length;
  /** @type {number[]} */
  let row = [];
  for (let j = 0; j <= n; j += 1) row[j] = j;
  for (let i = 1; i <= m; i += 1) {
    let prev = row[0];
    row[0] = i;
    for (let j = 1; j <= n; j += 1) {
      const cur = row[j];
      const cost = s[i - 1] === t[j - 1] ? 0 : 1;
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, prev + cost);
      prev = cur;
    }
  }
  const dist = row[n];
  return 1 - dist / Math.max(m, n);
}

/**
 * Kombinierte Ähnlichkeit für Namen/Kurzteile (Tippfehler, Buchstabendreher).
 * @param {string} a
 * @param {string} b
 */
export function fuzzyTokenSimilarity(a, b) {
  const x = String(a ?? "").trim();
  const y = String(b ?? "").trim();
  const ax = foldContactSearchAscii(x);
  const by = foldContactSearchAscii(y);
  if (!ax.length || !by.length) return 0;
  if (ax === by) return 1;
  const tri = trigramDiceSimilarity(ax, by);
  let lev = tri;
  if (Math.min(ax.length, by.length) <= 8) {
    lev = Math.max(lev, levenshteinRatio(ax, by));
  }
  return lev;
}

function isDominantlyNumericToken(tok) {
  const t = String(tok ?? "");
  const digits = t.replace(/\D/g, "");
  if (digits.length < 4) return false;
  return digits.length / Math.max(t.length, 1) >= 0.55;
}

/**
 * Mindest-Ähnlichkeit für Fuzzy (levenshtein/trigram-Kombination), tokenlängenabhängig.
 * Kurze Queries bleiben etwas toleranter; ab 4 Zeichen strikt (verwässert die Liste nicht).
 */
export const CONTACT_SEARCH_FUZZY_MIN_SIM = {
  /** Länge 2 */
  T2: 0.88,
  /** Länge 3 */
  T3: 0.66,
  /** Länge ≥ 4 */
  T4: 0.78,
};

/**
 * @typedef {{
 *   foldedFull: string,
 *   digitFull: string,
 *   primaryFolded: string,
 *   primaryWords?: string[],
 *   words: string[],
 * }} ContactSearchCorpus
 */

/**
 * Pro Such-Token: Match-Score (höher = besser). null = kein Treffer.
 * Ranking: Primärname → Prefix Primär → Fremdwort exakt (Billing/Beziehung) → Prefix dort →
 * Teilstring Primär → Teilstring Gesamt → Fuzzy (nur über einzelne Wörter, hohe Schwelle).
 * @param {string} foldedToken
 * @param {ContactSearchCorpus} corpus
 * @returns {{ score: number, kind: string } | null}
 */
export function matchContactSearchToken(foldedToken, corpus) {
  const tok = String(foldedToken ?? "").trim();
  if (!tok) return null;

  const { foldedFull, digitFull, primaryFolded } = corpus;
  const words = Array.isArray(corpus.words) ? corpus.words : extractFoldedCorpusWords(foldedFull);
  const primaryWords = Array.isArray(corpus.primaryWords)
    ? corpus.primaryWords
    : extractFoldedCorpusWords(primaryFolded);

  if (isDominantlyNumericToken(tok)) {
    let td = digitsOnlyFromString(tok);
    if (td.length >= 3 && digitFull.includes(td)) {
      return { score: 962, kind: "phone" };
    }
    // Lokale Vorwahl führende 0 / gemischte Formate (0170… vs +49… im Korpus)
    td = td.replace(/^0+/, "") || td;
    if (td.length >= 6 && digitFull.includes(td)) {
      return { score: 962, kind: "phone" };
    }
  }

  /** @param {string} w */
  const isPrimaryWord = (w) => primaryWords.includes(w);

  // 1) Ganzes Wort im eigenen Namen (Vor-/Nachname)
  if (primaryWords.some((w) => w === tok)) {
    return { score: 1250, kind: "primary-word-exact" };
  }

  // 2) Präfix auf Vor-/Nachnamens-Wörtern ("sör" → "sören")
  if (tok.length >= 2 && primaryWords.some((w) => w.startsWith(tok))) {
    return { score: 1220, kind: "primary-prefix" };
  }

  // 3) Exaktes Wort nur außerhalb Primär (Billing/Beziehung/… im Haystack-Wortliste)
  if (words.some((w) => w === tok && !isPrimaryWord(w))) {
    return { score: 1210, kind: "related-word-exact" };
  }

  // 4) Präfix auf nicht-primären Wörtern (z. B. verknüpfter Kontakt)
  if (tok.length >= 2 && words.some((w) => w.startsWith(tok) && !isPrimaryWord(w))) {
    return { score: 1190, kind: "related-prefix" };
  }

  // 5–6) Teilstring nur bei ausreichender Tokenlänge (kein "so" → halbe Liste)
  if (tok.length >= 4) {
    if (primaryFolded.includes(tok)) {
      return { score: 1170, kind: "partial-primary" };
    }
    if (foldedFull.includes(tok)) {
      return { score: 1150, kind: "partial-full" };
    }
  }

  // Einzelbuchstabe: nur kurze Wörter (wie zuvor)
  if (tok.length === 1) {
    for (const w of words) {
      if (w.length <= 4 && w.startsWith(tok)) {
        return { score: 740, kind: "prefix-short" };
      }
    }
    return null;
  }

  // Fuzzy nur über komplette Corpus-Wörter (keine Sliding-Windows in langen Notizen)
  let bestSim = 0;
  for (const w of words) {
    if (w.length < 2) continue;
    bestSim = Math.max(bestSim, fuzzyTokenSimilarity(tok, w));
  }

  const minSim =
    tok.length >= 4 ? CONTACT_SEARCH_FUZZY_MIN_SIM.T4 : tok.length === 3 ? CONTACT_SEARCH_FUZZY_MIN_SIM.T3 : CONTACT_SEARCH_FUZZY_MIN_SIM.T2;

  if (bestSim >= minSim) {
    return { score: 520 + Math.round(bestSim * 260), kind: "fuzzy" };
  }

  return null;
}

/**
 * Zerlegt die Nutzer-Eingabe in gefaltete Such-Tokens (Und-Verknüpfung).
 * @param {unknown} query
 * @returns {string[]}
 */
export function tokenizeContactSearchQuery(query) {
  const raw = String(query ?? "").trim();
  if (!raw) return [];
  const digits = digitsOnlyFromString(raw);
  const compactLen = raw.replace(/\s+/g, "").length;
  if (digits.length >= 6 && compactLen > 0 && digits.length / compactLen >= 0.52) {
    return [digits];
  }
  return raw
    .split(/\s+/)
    .filter(Boolean)
    .map((t) => foldContactSearchAscii(t))
    .filter(Boolean);
}

/**
 * @param {unknown} query
 * @param {ContactSearchCorpus} corpus
 * @returns {{ match: boolean, bottleneck: number, sum: number }}
 */
export function scoreContactSearchQuery(query, corpus) {
  const tokens = tokenizeContactSearchQuery(query);
  if (!tokens.length) {
    return { match: true, bottleneck: 10000, sum: 10000 };
  }

  let bottleneck = Infinity;
  let sum = 0;
  for (const tok of tokens) {
    const m = matchContactSearchToken(tok, corpus);
    if (!m) {
      return { match: false, bottleneck: 0, sum: 0 };
    }
    bottleneck = Math.min(bottleneck, m.score);
    sum += m.score;
  }
  return { match: true, bottleneck, sum };
}
